import type { ApolloClient } from '@apollo/client';

import { createGatewayErrorResolver } from '@/shared-responder-v2';
import {
  ADD_SCREENER_QUESTION_ANSWER,
  ADD_SCREENER_SESSION,
  type AddScreenerSessionMutation,
  type ScreenerQuestionType,
  type ScreenerSessionStatus,
} from '@/survey-graphql';

import { SCREENER_FLOW_GATEWAY_ERROR_CODES } from './shared-responder-v2-gateway-error.consts';
import { toChannel } from './shared-responder-v2-gateway-gateway';

const gatewayErrors = createGatewayErrorResolver(
  SCREENER_FLOW_GATEWAY_ERROR_CODES
);

interface ScreenerOption {
  readonly id: string;
  readonly label: string;
  readonly value: string;
}

export interface ScreenerQuestion {
  readonly id: string;
  readonly text: string;
  readonly type: ScreenerQuestionType;
  readonly questionOrder: number;
  readonly options: readonly ScreenerOption[];
}

export interface StartScreenerSessionInput {
  readonly projectId: string;
  readonly channel: string | null;
}

export interface StartScreenerSessionResult {
  readonly sessionId: string;
  readonly totalQuestions: number;
  readonly question: ScreenerQuestion;
}

export interface AnswerScreenerQuestionInput {
  readonly sessionId: string;
  readonly answer: string;
}

export interface AnswerScreenerQuestionResult {
  readonly status: ScreenerSessionStatus;
  readonly question: ScreenerQuestion | null;
  readonly projectSessionKey: string | null;
  readonly disqualificationRedirectUrl: string | null;
}

/**
 * Reads every field explicitly for the same reason `toTurnPayload` does in
 * `survey-responder-v2-gateway.adapter.ts`: a backend rename becomes a compile
 * error here instead of a silently-null render.
 */
function toScreenerQuestion(
  question: AddScreenerSessionMutation['addScreenerSession']['question']
): ScreenerQuestion {
  return {
    id: question.id,
    text: question.text,
    type: question.type,
    questionOrder: question.questionOrder,
    options: question.options.map((option) => ({
      id: option.id,
      label: option.label,
      value: option.value,
    })),
  };
}

/**
 * Starts a screener session for the project. Not idempotent — a second call
 * mints a second session — but an orphaned session is inert server-side (it
 * expires after 10 days and stores no answers), so callers only latch against
 * double effects, never retry loops.
 */
export async function startScreenerSession(
  client: ApolloClient,
  input: StartScreenerSessionInput
): Promise<StartScreenerSessionResult> {
  const result = await client.mutate({
    mutation: ADD_SCREENER_SESSION,
    variables: {
      input: {
        projectId: input.projectId,
        channel: toChannel(input.channel),
      },
    },
    errorPolicy: 'all',
  });

  gatewayErrors.throwIfGatewayError(result.error);
  if (!result.data) {
    throw new Error('addScreenerSession returned no data');
  }

  return {
    sessionId: result.data.addScreenerSession.sessionId,
    totalQuestions: result.data.addScreenerSession.totalQuestions,
    question: toScreenerQuestion(result.data.addScreenerSession.question),
  };
}

/**
 * Submits the current question's answer. The backend advances its own cursor,
 * so the caller renders exactly what comes back: another `question` while
 * `status` is active, a `projectSessionKey` once completed, or a redirect
 * target when disqualified.
 */
export async function answerScreenerQuestion(
  client: ApolloClient,
  input: AnswerScreenerQuestionInput
): Promise<AnswerScreenerQuestionResult> {
  const result = await client.mutate({
    mutation: ADD_SCREENER_QUESTION_ANSWER,
    variables: { input },
    errorPolicy: 'all',
  });

  gatewayErrors.throwIfGatewayError(result.error);
  if (!result.data) {
    throw new Error('addScreenerQuestionAnswer returned no data');
  }

  const answer = result.data.addScreenerQuestionAnswer;

  return {
    status: answer.status,
    question: answer.question ? toScreenerQuestion(answer.question) : null,
    projectSessionKey: answer.projectSessionKey ?? null,
    disqualificationRedirectUrl: answer.disqualificationRedirectUrl ?? null,
  };
}
